import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import type { DayBucket, TabKind } from '../model/contracts'

const BAR_COUNT = 30
const CHART_HEIGHT = 80

interface DateHistogramProps {
  days: DayBucket[]
  tab: TabKind
  className?: string
}

export const DateHistogram = memo(function DateHistogram({
  days,
  tab,
  className,
}: DateHistogramProps) {
  const { t } = useTranslation('topicTrend')

  const values = days.map((d) => (tab === 'news' ? d.news : d.posts + d.news))
  const maxVal = Math.max(...values, 1)

  return (
    <div className={cn('rounded-xl border border-border bg-card p-4', className)}>
      <div className="mb-3 flex items-center justify-between">
        <span className="text-xs font-semibold text-muted-foreground">
          {t('histogram.30d')}
        </span>
      </div>

      {values.every((v) => v === 0) ? (
        <div className="flex h-20 items-center justify-center">
          <span className="text-xs text-muted-foreground">{t('empty')}</span>
        </div>
      ) : (
        <svg
          width="100%"
          height={CHART_HEIGHT}
          viewBox={`0 0 ${BAR_COUNT * 10} ${CHART_HEIGHT}`}
          preserveAspectRatio="none"
          aria-label={t('histogram.30d')}
          role="img"
        >
          {days.slice(0, BAR_COUNT).map((d, i) => {
            const val = tab === 'news' ? d.news : d.posts + d.news
            const barH = Math.round((val / maxVal) * (CHART_HEIGHT - 4))
            const x = i * 10 + 1
            const y = CHART_HEIGHT - barH
            return (
              <rect
                key={d.date}
                x={x}
                y={y}
                width={8}
                height={barH}
                rx={2}
                className="fill-primary/70"
              />
            )
          })}
        </svg>
      )}

      {days.length >= 2 && (
        <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
          <span>{days[0]?.date?.slice(5) ?? ''}</span>
          <span>{days[days.length - 1]?.date?.slice(5) ?? ''}</span>
        </div>
      )}
    </div>
  )
})
